import { forwardRef, useContext, useEffect, useImperativeHandle, useRef } from 'react';
import { GoogleMapsContext, useMapsLibrary } from '@vis.gl/react-google-maps';

function usePolyline(props) {
  const { onClick, onDrag, onDragStart, onDragEnd, onMouseOver, onMouseOut, encodedPath, ...polylineOptions } = props;

  // This is here to avoid triggering the useEffect below when the callbacks change (which happen if the user didn't memoize them)
  const callbacks = useRef({});
  Object.assign(callbacks.current, {
    onClick,
    onDrag,
    onDragStart,
    onDragEnd,
    onMouseOver,
    onMouseOut,
  });

  const geometryLibrary = useMapsLibrary('geometry');

  const polylineRef = useRef(null);

  const map = useContext(GoogleMapsContext)?.map;

  // Create polyline only after map is available
  useEffect(() => {
    if (!map) return;

    if (!polylineRef.current) {
      try {
        polylineRef.current = new google.maps.Polyline();
      } catch (e) {
        console.warn('[Polyline] Failed to create Polyline instance:', e);
        return;
      }
    }

    polylineRef.current.setMap(map);

    return () => {
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
      }
    };
  }, [map]);

  // Update PolylineOptions
  useEffect(() => {
    if (!polylineRef.current) return;
    try {
      polylineRef.current.setOptions(polylineOptions);
    } catch (e) {
      // Ignore options errors
    }
  }, [polylineOptions]);

  // Update the path with the encodedPath
  useEffect(() => {
    if (!encodedPath || !geometryLibrary || !map || !polylineRef.current) {
      return;
    }
    try {
      const path = geometryLibrary.encoding.decodePath(encodedPath);
      polylineRef.current.setPath(path);
    } catch (e) {
      console.warn('[Polyline] Failed to set path:', e);
    }
  }, [encodedPath, geometryLibrary, map]);

  // Attach and re-attach event-handlers when any of the properties change
  useEffect(() => {
    if (!polylineRef.current) return;

    // Add event listeners
    const gme = google.maps.event;
    const eventHandlers = [
      ['click', 'onClick'],
      ['drag', 'onDrag'],
      ['dragstart', 'onDragStart'],
      ['dragend', 'onDragEnd'],
      ['mouseover', 'onMouseOver'],
      ['mouseout', 'onMouseOut'],
    ];

    eventHandlers.forEach(([eventName, eventCallback]) => {
      gme.addListener(polylineRef.current, eventName, (e) => {
        const callback = callbacks.current[eventCallback];
        if (callback) callback(e);
      });
    });

    return () => {
      gme.clearInstanceListeners(polylineRef.current);
    };
  }, [map]);

  return polylineRef.current;
}

/**
 * Component to render a polyline on a map
 */
export const Polyline = forwardRef((props, ref) => {
  const polyline = usePolyline(props);

  useImperativeHandle(ref, () => polyline, [polyline]);

  return null;
});
